'use client'

import { env } from '@/env'
import { AnalyzifyProvider as SDKAnalyzifyProvider } from 'analyzify-js/react'

import { AnalyzifyMode, MOCK_CONFIG } from './Analyzify.constants'

/**
 * Mounts the Analyzify SDK, or renders children untouched when disabled.
 *
 * The two domains are deliberately different values. storefrontRootDomain is the
 * cookie domain: the SDK dot-prefixes it to '.supagas.com.au' so the checkout can
 * read cookies the storefront set. checkoutRootDomain is the host Shopify's
 * consent-tracking-api sends Storefront API requests to, so it has to be a
 * Shopify-served domain. Our apex is the Vercel app and answers those with a
 * redirect, which is why passing one value to both left consent unwritten.
 *
 * Note we never pass `gtmContainerId`. The GTM container is loaded once by
 * <GoogleTagManager> in app/layout.tsx; handing the ID to the SDK as well would
 * load GTM-PLGCV3B twice. The SDK only pushes into the dataLayer that already exists.
 */
export const AnalyzifyProvider = ({ children }: { children: React.ReactNode }) => {
  const mode = env.NEXT_PUBLIC_ANALYZIFY_MODE

  if (mode === AnalyzifyMode.OFF) return <>{children}</>

  // installAllShims() does not strip the protocol the way config-fetcher does,
  // so normalise it here - our env value is stored with the https:// prefix.
  const shopDomain = env.NEXT_PUBLIC_SHOPIFY_STORE_DOMAIN.replace(/^https?:\/\//, '').replace(/\/$/, '')

  const isMock = mode === AnalyzifyMode.MOCK

  return (
    <SDKAnalyzifyProvider
      shopDomain={shopDomain}
      // `config` short-circuits the Storefront metafield fetch entirely.
      config={isMock ? MOCK_CONFIG : undefined}
      storefrontToken={isMock ? undefined : env.NEXT_PUBLIC_SHOPIFY_STOREFRONT_TOKEN}
      currency="AUD"
      country="AU"
      locale="en"
      storefrontRootDomain={env.NEXT_PUBLIC_ANALYZIFY_ROOT_DOMAIN}
      checkoutRootDomain={env.NEXT_PUBLIC_ANALYZIFY_CHECKOUT_DOMAIN}
      debug={isMock}
    >
      {children}
    </SDKAnalyzifyProvider>
  )
}
